/**
 * qocAssessmentExcel.js
 * Export assessment MEs to a well-formatted Excel with dropdowns,
 * then import the filled file back to resume online.
 *
 * Place in: web/src/utils/qocAssessmentExcel.js
 *
 * Dependencies:
 *   npm install exceljs file-saver
 *   (xlsx is already in most React projects — used for import parsing only)
 *
 * Why ExcelJS instead of xlsx-js-style:
 *   xlsx-js-style silently drops cell-level protection, freeze panes, and
 *   data-validation dropdowns.  ExcelJS supports all three natively and
 *   produces files that open cleanly in Microsoft Excel without repair prompts.
 */
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';

// ── Options ───────────────────────────────────────────────────────────────────
export const FULFILLMENT_OPTIONS = ['Met', 'Partially Met', 'Not Met', 'Not Applicable'];
export const CRITICALITY_OPTIONS = ['High', 'Medium', 'Low', 'N/A'];

// DB stores criticality in lowercase (high/medium/low/n/a).
// Normalise to the canonical form expected by Excel validation and import.
function normaliseCriticality(val) {
    if (!val) return '';
    const map = { high:'High', medium:'Medium', low:'Low', 'n/a':'N/A', na:'N/A' };
    return map[String(val).trim().toLowerCase()] || val;
}

// Same 90/50/49 thresholds used online in recalculate() — kept as one
// shared function so the offline-suggested score can never silently drift
// from what the platform would compute for the same checked/total ratio.
function suggestFulfillmentFromChecklist(checkedCount, totalCount) {
    if (totalCount <= 0) return '';
    const pct = (checkedCount / totalCount) * 100;
    if (pct >= 90) return 'Met';
    if (pct >= 50) return 'Partially Met';
    return 'Not Met';
}

// checklist_state may arrive as a JSON string (straight from the DB column)
// or an already-parsed array (if some upstream caller parsed it first) —
// handle both so this utility doesn't assume which.
function parseChecklistState(val) {
    if (Array.isArray(val)) return val.map(Number);
    if (typeof val === 'string' && val.trim()) {
        try {
            const parsed = JSON.parse(val);
            return Array.isArray(parsed) ? parsed.map(Number) : [];
        } catch (e) { return []; }
    }
    return [];
}

// ── Colour palette (ARGB — Excel wants alpha prefix) ──────────────────────────
const C = {
    navy:       'FF0F172A',
    navyLight:  'FF1E3A5F',
    white:      'FFFFFFFF',
    hdrScore:   'FF1B5E20',
    hdrCrit:    'FFBF360C',
    hdrEdit:    'FF1E3A5F',
    hdrCheck:   'FF6D28D9',
    lockedBg:   'FFF0F4FF',
    lockedAlt:  'FFEBF0FF',
    scoreBg:    'FFFFFDE7',
    scoreAlt:   'FFFFF8DC',
    critBg:     'FFFFF3E0',
    critAlt:    'FFFFF8F0',
    editBg:     'FFFFFFFF',
    editAlt:    'FFF8FAFF',
    checkBg:    'FFF5F3FF',
    checkAlt:   'FFEDE9FE',
    border:     'FFD0D7E3',
    text:       'FF2D3748',
    textEdit:   'FF374151',
    instrBg:    'FFF0F5FF',
    instrTitle: 'FF0F172A',
};

// ── Style helpers ─────────────────────────────────────────────────────────────
const thinBorder = {
    top:    { style: 'thin', color: { argb: C.border } },
    bottom: { style: 'thin', color: { argb: C.border } },
    left:   { style: 'thin', color: { argb: C.border } },
    right:  { style: 'thin', color: { argb: C.border } },
};

function styleHeader(cell, bgArgb) {
    cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgArgb } };
    cell.font      = { bold: true, color: { argb: C.white }, size: 10, name: 'Arial' };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border    = thinBorder;
    cell.protection = { locked: true };
}

function styleLocked(cell, alt) {
    cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: alt ? C.lockedAlt : C.lockedBg } };
    cell.font      = { size: 9, name: 'Arial', color: { argb: C.text } };
    cell.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
    cell.border    = thinBorder;
    cell.protection = { locked: true };
}

function styleScore(cell, alt) {
    cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: alt ? C.scoreAlt : C.scoreBg } };
    cell.font      = { size: 10, bold: true, name: 'Arial', color: { argb: C.navy } };
    cell.alignment = { horizontal: 'center', vertical: 'top', wrapText: false };
    cell.border    = thinBorder;
    cell.protection = { locked: false };
}

function styleEdit(cell, alt) {
    cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: alt ? C.editAlt : C.editBg } };
    cell.font      = { size: 9, name: 'Arial', color: { argb: C.textEdit } };
    cell.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
    cell.border    = thinBorder;
    cell.protection = { locked: false };
}

function styleCrit(cell, alt) {
    cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: alt ? C.critAlt : C.critBg } };
    cell.font      = { size: 9, name: 'Arial', color: { argb: C.textEdit } };
    cell.alignment = { horizontal: 'center', vertical: 'top', wrapText: false };
    cell.border    = thinBorder;
    cell.protection = { locked: false };
}

function styleCheck(cell, alt) {
    cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: alt ? C.checkAlt : C.checkBg } };
    cell.font      = { size: 9, bold: true, name: 'Arial', color: { argb: C.textEdit } };
    cell.alignment = { horizontal: 'center', vertical: 'top', wrapText: false };
    cell.border    = thinBorder;
    cell.protection = { locked: false };
}

// ── EXPORT ────────────────────────────────────────────────────────────────────
export async function exportAssessmentExcel({
    facility, schedule, assessment, mes, responses = {}, assessmentDate
}) {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'QoC Platform';
    wb.created = new Date();

    const totalRows = mes.length + 1;

    // MEs that actually carry a reviewed checklist — everything else behaves
    // exactly as before, with no Checklist sheet generated at all if none do.
    const mesWithChecklist = mes.filter(me => Array.isArray(me.checklist_items) && me.checklist_items.length > 0);

    // ══════════════════════════════════════════════════════════════════════════
    // SHEET 1 — Instructions
    // ══════════════════════════════════════════════════════════════════════════
    const instrWs = wb.addWorksheet('Instructions');
    instrWs.columns = [{ width: 80 }];

    const instrLines = [
        { text: 'QoC Assessment — Offline Scoring Instructions', bold: true, size: 13, bg: C.navy, fg: C.white },
        { text: '' },
        { text: 'HOW TO USE THIS FILE', bold: true, size: 11, bg: C.instrBg, fg: C.instrTitle },
        { text: '1.  Go to the "Assessment" sheet (tab at the bottom).' },
        { text: '2.  Column D (Score) — click a cell and use the dropdown: Met / Partially Met / Not Met / Not Applicable.' },
        { text: '3.  Column G (Criticality) — use the dropdown: High / Medium / Low / N/A.' },
        { text: '4.  Columns E, F, H, I — free-text. Type comments, observations, evidence.' },
        ...(mesWithChecklist.length > 0 ? [
            { text: '' },
            { text: 'CHECKLIST SHEET (some MEs only)', bold: true, size: 11, bg: C.instrBg, fg: C.instrTitle },
            { text: '5.  Some MEs have a "Checklist" sheet tab with specific items to verify.' },
            { text: '6.  Mark each item Y or N in the Checked column.' },
            { text: '7.  If you leave that ME\'s Score cell blank, it will be worked out automatically from how many' },
            { text: '     items you checked when you import this file back (90%+ = Met, 50-89% = Partially Met, else Not Met).' },
            { text: '8.  If you type a Score yourself, that is always respected — the checklist is only a suggestion.' },
        ] : []),
        { text: '' },
        { text: 'WHAT NOT TO TOUCH', bold: true, size: 11, bg: C.instrBg, fg: C.instrTitle },
        { text: '⚠  Columns A, B, C (Ref No, Sub-Requirement, Measurable Element) are read-only.', bg: 'FFFFF2F0', fg: 'FFB71C1C' },
        { text: '⚠  The header row (row 1) is read-only.', bg: 'FFFFF2F0', fg: 'FFB71C1C' },
        { text: '⚠  Do not rename or delete sheets.', bg: 'FFFFF2F0', fg: 'FFB71C1C' },
        { text: '' },
        { text: 'IMPORTING BACK', bold: true, size: 11, bg: C.instrBg, fg: C.instrTitle },
        { text: '9.  Save this file when done.' },
        { text: '10. Return to the QoC platform, open the same assessment, click "Import Excel".' },
        { text: '11. Select this file — responses will be merged into the online form automatically.' },
        { text: '' },
        {
            text: `Facility: ${facility?.business_name || ''}   |   Clause: ${schedule?.clause_code || ''}   |   Date: ${moment(assessmentDate).format('DD MMMM YYYY')}`,
            bold: true, bg: C.instrBg, fg: C.instrTitle,
        },
    ];

    instrLines.forEach(line => {
        const row  = instrWs.addRow([line.text || '']);
        row.height = line.size ? 24 : 18;
        const cell = row.getCell(1);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: line.bg || C.white } };
        cell.font = { bold: line.bold || false, size: line.size || 10, color: { argb: line.fg || C.text }, name: 'Arial' };
        cell.alignment = { vertical: 'middle', wrapText: true };
        cell.protection = { locked: true };
    });

    await instrWs.protect('', { selectLockedCells: true, selectUnlockedCells: false });

    // ══════════════════════════════════════════════════════════════════════════
    // SHEET 2 — Assessment
    // ══════════════════════════════════════════════════════════════════════════
    const asmtWs = wb.addWorksheet('Assessment');
    asmtWs.columns = [
        { key: 'ref_no',      width: 8  },
        { key: 'sub_req',     width: 22 },
        { key: 'me_text',     width: 40 },
        { key: 'score',       width: 16 },
        { key: 'comment',     width: 32 },
        { key: 'gen_obs',     width: 32 },
        { key: 'criticality', width: 14 },
        { key: 'good_prac',   width: 32 },
        { key: 'evidence',    width: 30 },
    ];

    // Header row
    asmtWs.getRow(1).height = 32;
    [
        ['A1', 'Ref No',                 C.navy],
        ['B1', 'Sub-Requirement',        C.navy],
        ['C1', 'Measurable Element',     C.navy],
        ['D1', 'Score  ▼',              C.hdrScore],
        ['E1', 'Comment',                C.hdrEdit],
        ['F1', 'General Observation',    C.hdrEdit],
        ['G1', 'Criticality  ▼',        C.hdrCrit],
        ['H1', 'Area of Good Practice',  C.hdrEdit],
        ['I1', 'Evidence Notes',         C.hdrEdit],
    ].forEach(([addr, label, bg]) => {
        const cell = asmtWs.getCell(addr);
        cell.value = label;
        styleHeader(cell, bg);
    });

    // Data rows
    mes.forEach((me, i) => {
        const row = i + 2;
        const alt = i % 2 === 1;
        const r   = responses[me.id] || {};
        asmtWs.getRow(row).height = 55;

        const defs = [
            ['A', me.ref_no || '',                              styleLocked],
            ['B', me.sub_requirement || '',                     styleLocked],
            ['C', me.me_text || me.question_text || '',         styleLocked],
            ['D', r.fulfillment || '',                          styleScore],
            ['E', r.comment || '',                              styleEdit],
            ['F', r.general_observation || '',                  styleEdit],
            ['G', normaliseCriticality(r.criticality_of_observation), styleCrit],
            ['H', r.area_of_good_practice || '',               styleEdit],
            ['I', r.evidence_notes || '',                      styleEdit],
        ];
        defs.forEach(([col, value, styleFn]) => {
            const cell = asmtWs.getCell(`${col}${row}`);
            cell.value = value;
            styleFn(cell, alt);
        });
    });

    // Dropdowns
    asmtWs.dataValidations.add(`D2:D${totalRows}`, {
        type: 'list', allowBlank: true,
        formulae: [`"${FULFILLMENT_OPTIONS.join(',')}"`],
        showErrorMessage: true, errorStyle: 'stop',
        errorTitle: 'Invalid Score',
        error: `Must be one of: ${FULFILLMENT_OPTIONS.join(', ')}`,
        showInputMessage: true, promptTitle: 'Score',
        prompt: 'Select: Met, Partially Met, Not Met, or Not Applicable',
    });
    asmtWs.dataValidations.add(`G2:G${totalRows}`, {
        type: 'list', allowBlank: true,
        formulae: [`"${CRITICALITY_OPTIONS.join(',')}"`],
        showErrorMessage: true, errorStyle: 'stop',
        errorTitle: 'Invalid Criticality',
        error: `Must be one of: ${CRITICALITY_OPTIONS.join(', ')}`,
        showInputMessage: true, promptTitle: 'Criticality of Observation',
        prompt: 'Select: High, Medium, Low, or N/A',
    });

    // Freeze: columns A–C + header row always visible
    asmtWs.views = [{ state: 'frozen', xSplit: 3, ySplit: 1, topLeftCell: 'D2' }];

    // Sheet protection — cells with locked:false remain editable
    await asmtWs.protect('', {
        selectLockedCells:   true,
        selectUnlockedCells: true,
        formatCells:         false,
        formatColumns:       false,
        formatRows:          false,
        insertColumns:       false,
        insertRows:          false,
        insertHyperlinks:    false,
        deleteColumns:       false,
        deleteRows:          false,
        sort:                false,
        autoFilter:          false,
        pivotTables:         false,
    });

    // ══════════════════════════════════════════════════════════════════════════
    // SHEET 3 — Checklist (only if at least one ME has a reviewed checklist)
    // One row per (ME, item) — a flat layout handles any item count per ME
    // without needing a fixed number of columns.
    // ══════════════════════════════════════════════════════════════════════════
    if (mesWithChecklist.length > 0) {
        const clWs = wb.addWorksheet('Checklist');
        clWs.columns = [
            { key: 'me_id',    width: 10 },
            { key: 'ref_no',   width: 10 },
            { key: 'item_id',  width: 10 },
            { key: 'item_text',width: 55 },
            { key: 'checked',  width: 12 },
        ];

        clWs.getRow(1).height = 26;
        [
            ['A1', 'ME ID',      C.navy],
            ['B1', 'Ref No',     C.navy],
            ['C1', 'Item ID',    C.navy],
            ['D1', 'Item Text',  C.navy],
            ['E1', 'Checked ▼', C.hdrCheck],
        ].forEach(([addr, label, bg]) => {
            const cell = clWs.getCell(addr);
            cell.value = label;
            styleHeader(cell, bg);
        });

        let clRow = 2;
        mesWithChecklist.forEach((me, meIdx) => {
            const alt = meIdx % 2 === 1;
            const r = responses[me.id] || {};
            const checkedIds = new Set(parseChecklistState(r.checklist_state));
            (me.checklist_items || []).forEach(item => {
                const isChecked = checkedIds.has(Number(item.id));
                const defs = [
                    ['A', me.id,                       styleLocked],
                    ['B', me.ref_no || '',              styleLocked],
                    ['C', item.id,                      styleLocked],
                    ['D', item.item_text || '',         styleLocked],
                    ['E', isChecked ? 'Y' : 'N',        styleCheck],
                ];
                defs.forEach(([col, value, styleFn]) => {
                    const cell = clWs.getCell(`${col}${clRow}`);
                    cell.value = value;
                    styleFn(cell, alt);
                });
                clRow++;
            });
        });

        const lastClRow = clRow - 1;
        if (lastClRow >= 2) {
            clWs.dataValidations.add(`E2:E${lastClRow}`, {
                type: 'list', allowBlank: true,
                formulae: ['"Y,N"'],
                showErrorMessage: true, errorStyle: 'stop',
                errorTitle: 'Invalid value',
                error: 'Must be Y or N',
                showInputMessage: true, promptTitle: 'Checked?',
                prompt: 'Y if this item was verified, N if not',
            });
        }

        clWs.views = [{ state: 'frozen', xSplit: 4, ySplit: 1, topLeftCell: 'E2' }];

        await clWs.protect('', {
            selectLockedCells:   true,
            selectUnlockedCells: true,
            formatCells:         false,
            formatColumns:       false,
            formatRows:          false,
            insertColumns:       false,
            insertRows:          false,
            insertHyperlinks:    false,
            deleteColumns:       false,
            deleteRows:          false,
            sort:                false,
            autoFilter:          false,
            pivotTables:         false,
        });
    }

    // ══════════════════════════════════════════════════════════════════════════
    // SHEET 4 — Meta (locked import mapping)
    // ══════════════════════════════════════════════════════════════════════════
    const metaWs = wb.addWorksheet('Meta');
    metaWs.columns = [{ width: 20 }, { width: 15 }, { width: 15 }];

    [
        ['META - DO NOT EDIT THIS SHEET', ''],
        ['assessment_id',   assessment?.id          || ''],
        ['schedule_id',     schedule?.id            || ''],
        ['facility_id',     facility?.id            || ''],
        ['clause_code',     schedule?.clause_code   || ''],
        ['assessment_date', assessmentDate || moment().format('YYYY-MM-DD')],
        ['me_count',        mes.length],
        ['', ''],
        ['row_index', 'me_id', 'ref_no'],
        ...mes.map((me, i) => [i + 2, me.id, me.ref_no]),
    ].forEach(rowData => {
        const row = metaWs.addRow(rowData);
        row.eachCell(cell => { cell.protection = { locked: true }; });
    });

    await metaWs.protect('', { selectLockedCells: true, selectUnlockedCells: false });

    // ── Download ──────────────────────────────────────────────────────────────
    const safeFacility = (facility?.business_name || 'Facility').replace(/[^A-Za-z0-9]/g, '_');
    const safeClause   = schedule?.clause_code || 'Assessment';
    const dateStr      = moment(assessmentDate).format('YYYY-MM-DD');
    const filename     = `QoC_Assessment_${safeFacility}_${safeClause}_${dateStr}.xlsx`;

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
        new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        filename
    );
    return filename;
}

// ── IMPORT ────────────────────────────────────────────────────────────────────
// context (optional) — { facility_id, clause_code, currentAnsweredCount }
export async function importAssessmentExcel(file, context = {}) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                // Use plain xlsx for import (lighter, read-only)
                const XLSX = require('xlsx');
                const data = new Uint8Array(e.target.result);
                const wb   = XLSX.read(data, { type: 'array' });
                const errors = [];

                // ── Meta sheet ────────────────────────────────────────────────
                const metaWs = wb.Sheets['Meta'];
                if (!metaWs) {
                    reject(new Error('Invalid file: Meta sheet missing. Please use the original exported file.'));
                    return;
                }
                const metaRows = XLSX.utils.sheet_to_json(metaWs, { header: 1 });
                const metaMap  = {};
                const meIdMap  = {};
                let inMeSection = false;
                for (const row of metaRows) {
                    if (!row[0]) continue;
                    if (row[0] === 'row_index') { inMeSection = true; continue; }
                    if (inMeSection && row[0] && row[1]) {
                        meIdMap[parseInt(row[0])] = row[1];
                    } else if (!inMeSection && row[1] !== undefined) {
                        metaMap[String(row[0]).trim()] = row[1];
                    }
                }

                // ── Guard 1: Wrong facility ───────────────────────────────────
                if (
                    context.facility_id &&
                    metaMap['facility_id'] &&
                    String(metaMap['facility_id']) !== String(context.facility_id)
                ) {
                    reject(new Error(
                        'This file was exported for a different facility and cannot be imported here. ' +
                        'Please use the file that was exported from this assessment.'
                    ));
                    return;
                }

                // ── Guard 2: Wrong clause ─────────────────────────────────────
                if (
                    context.clause_code &&
                    metaMap['clause_code'] &&
                    String(metaMap['clause_code']).trim().toLowerCase() !==
                    String(context.clause_code).trim().toLowerCase()
                ) {
                    reject(new Error(
                        `This file was exported for clause "${metaMap['clause_code']}" ` +
                        `but this assessment is for clause "${context.clause_code}". ` +
                        'Please import the correct file.'
                    ));
                    return;
                }

                // ── Assessment sheet ──────────────────────────────────────────
                const asmtWs = wb.Sheets['Assessment'];
                if (!asmtWs) {
                    reject(new Error('Invalid file: Assessment sheet missing.'));
                    return;
                }
                const rows = XLSX.utils.sheet_to_json(asmtWs, { header: 1, defval: '' });
                if (rows.length < 2) {
                    reject(new Error('Assessment sheet has no data rows.'));
                    return;
                }

                const header = rows[0].map(h => String(h).trim());
                const ci = {
                    fulfillment:                header.findIndex(h => h.startsWith('Score')),
                    comment:                    header.indexOf('Comment'),
                    general_observation:        header.indexOf('General Observation'),
                    criticality_of_observation: header.findIndex(h => h.startsWith('Criticality')),
                    area_of_good_practice:      header.indexOf('Area of Good Practice'),
                    evidence_notes:             header.indexOf('Evidence Notes'),
                    ref_no:                     header.indexOf('Ref No'),
                };

                if (ci.fulfillment === -1) {
                    reject(new Error('Could not find "Score" column. File may be corrupted or from an older version.'));
                    return;
                }

                const responses = {};
                let answered = 0;

                for (let rowIdx = 1; rowIdx < rows.length; rowIdx++) {
                    const row      = rows[rowIdx];
                    const excelRow = rowIdx + 1;
                    const meId     = meIdMap[excelRow];
                    if (!meId) continue;

                    const fulfillment = String(row[ci.fulfillment] || '').trim();
                    // Normalise criticality — old exports and manual edits may have lowercase
                    const crit = normaliseCriticality(String(row[ci.criticality_of_observation] || '').trim());

                    if (fulfillment && !FULFILLMENT_OPTIONS.includes(fulfillment)) {
                        errors.push(`Row ${excelRow} (${row[ci.ref_no]}): Invalid score "${fulfillment}". Valid: ${FULFILLMENT_OPTIONS.join(', ')}`);
                    }
                    if (crit && !CRITICALITY_OPTIONS.includes(crit)) {
                        errors.push(`Row ${excelRow}: Invalid criticality "${crit}". Valid: ${CRITICALITY_OPTIONS.join(', ')}`);
                    }
                    const rowComment = String(row[ci.comment] || '').trim();
                    // Every scored ME needs a comment — matches the online rule
                    // (Met included, not just Partially Met/Not Met). This is a
                    // soft warning here (import still proceeds); the real block
                    // happens online at "Submit for Review" regardless.
                    if (fulfillment && !rowComment) {
                        errors.push('Row ' + excelRow + ' (' + row[ci.ref_no] + '): Comment required for ' + fulfillment);
                    }

                    responses[meId] = {
                        fulfillment,
                        comment:                    rowComment,
                        general_observation:        String(row[ci.general_observation] || '').trim(),
                        criticality_of_observation: crit,
                        area_of_good_practice:      String(row[ci.area_of_good_practice] || '').trim(),
                        evidence_notes:             String(row[ci.evidence_notes] || '').trim(),
                    };
                }

                // ── Checklist sheet (optional — only present if this export
                // included at least one ME with a reviewed checklist) ─────────
                const clWs = wb.Sheets['Checklist'];
                if (clWs) {
                    const clRows = XLSX.utils.sheet_to_json(clWs, { header: 1, defval: '' });
                    // Group checked/total per me_id from the flat item rows
                    const byMe = {}; // { [meId]: { checkedIds: [], totalIds: [] } }
                    for (let i = 1; i < clRows.length; i++) {
                        const row = clRows[i];
                        const meId    = parseInt(row[0]);
                        const itemId  = parseInt(row[2]);
                        const checked = String(row[4] || '').trim().toUpperCase() === 'Y';
                        if (!meId || !itemId) continue;
                        if (!byMe[meId]) byMe[meId] = { checkedIds: [], totalIds: [] };
                        byMe[meId].totalIds.push(itemId);
                        if (checked) byMe[meId].checkedIds.push(itemId);
                    }

                    Object.entries(byMe).forEach(([meIdStr, grp]) => {
                        const meId = parseInt(meIdStr);
                        if (!responses[meId]) {
                            // ME had checklist rows but no row at all on the
                            // Assessment sheet (shouldn't normally happen, but
                            // don't silently drop the checklist state if it does)
                            responses[meId] = {
                                fulfillment: '', comment: '', general_observation: '',
                                criticality_of_observation: '', area_of_good_practice: '',
                                evidence_notes: '',
                            };
                        }
                        responses[meId].checklist_state = grp.checkedIds;

                        // Score left blank → work it out from the checklist.
                        // Anything typed in Score is always respected as-is.
                        if (!responses[meId].fulfillment) {
                            const suggested = suggestFulfillmentFromChecklist(grp.checkedIds.length, grp.totalIds.length);
                            if (suggested) {
                                responses[meId].fulfillment = suggested;
                            }
                        }
                    });
                }

                // Recount "answered" after the checklist may have filled in
                // some blank Scores — otherwise these would look unanswered
                // even though they now have a real, checklist-derived value.
                answered = Object.values(responses).filter(r => r.fulfillment).length;

                // ── Guard 3: Regression check ─────────────────────────────────
                const currentAnswered = context.currentAnsweredCount ?? 0;
                if (currentAnswered > 0 && answered < currentAnswered) {
                    errors.push(`__REGRESSION__:${answered}:${currentAnswered}`);
                }

                resolve({
                    responses,
                    meta: {
                        assessment_id:   metaMap['assessment_id']   || null,
                        schedule_id:     metaMap['schedule_id']     || null,
                        facility_id:     metaMap['facility_id']     || null,
                        clause_code:     metaMap['clause_code']     || null,
                        assessment_date: metaMap['assessment_date'] || null,
                        me_count:        parseInt(metaMap['me_count'] || 0),
                        answered,
                    },
                    errors,
                });
            } catch (err) {
                reject(new Error('Failed to parse Excel file: ' + err.message));
            }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
    });
}